import type { CSSProperties, ReactElement, ReactNode } from "react";
import { motion, useTransform, type MotionValue } from "framer-motion";
import { Cimage } from "@/components/ui/Cimage";
import { FAKE_COMMENT_MESSAGE_KIND } from "@/components/ui/Cfakecomment";
import type { FakeCommentFileAttachment, FakeCommentMessage, } from "@/components/ui/Cfakecomment";
import type { ScrollCommentData } from "@/components/ui/Cchatteaser/Cchatteaser.data";
import { getProviderIconPath } from "@/variety/llmProviders";
import { CIRCLE_SIZE, MENTION_PROVIDER, MESSAGE_LINE_HEIGHT, MESSAGE_TEXT_GRADIENT, MINT, } from "./chatteaser3.constants";
import type { measureChatTeaser3Layout } from "./chatteaser3Layout";
import { Chatteaser3FileChip } from "./chatteaser3FileChip";
import { AVATAR_CAPSULE_BASE_STYLE, AVATAR_ICON_STYLE, AVATAR_NAME_STYLE, AVATAR_SLOT_STYLE, HEADER_MINT_STYLE, HEADER_MUTED_STYLE, MESSAGE_CONTENT_STYLE, MESSAGE_ROW_STYLE, ROLE_BRACKET_STYLE, ROLE_TAG_BG_STYLE, ROLE_TAG_INNER_STYLE, ROLE_TAG_WRAP_STYLE, ROLE_TEXT_STYLE, SECTION_HEADER_STYLE, } from "./chatteaser3.styles";
// ---------------------------------------------------------------------------
// Message text (Bare): smaller gradient text with minimal padding.
// ---------------------------------------------------------------------------
const MESSAGE_TEXT_BASE: CSSProperties = {
    margin: 0,
    backgroundImage: MESSAGE_TEXT_GRADIENT,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
    WebkitTextFillColor: "transparent",
    whiteSpace: "pre-line",
};
const MESSAGE_TEXT_STYLE: CSSProperties = {
    ...MESSAGE_TEXT_BASE,
    fontSize: "var(--ct3-message-font-size, 22px)",
    lineHeight: MESSAGE_LINE_HEIGHT,
};
const MESSAGE_TEXT_WRAP_STYLE: CSSProperties = {
    alignSelf: "flex-start",
    padding: "4px 2px",
};
// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------
interface SectionHeaderProps {
    first: string;
    second: string;
    mintFirst: boolean;
}
export function SectionHeader({ first, second, mintFirst, }: SectionHeaderProps): ReactElement {
    return (
      <div style={SECTION_HEADER_STYLE}>
        <span style={mintFirst ? HEADER_MINT_STYLE : HEADER_MUTED_STYLE}>
          {first}
        </span>
        <span style={mintFirst ? HEADER_MUTED_STYLE : HEADER_MINT_STYLE}>
          {second}
        </span>
      </div>
    );
}
// ---------------------------------------------------------------------------
// @mention chip (Pill — the chosen style)
// ---------------------------------------------------------------------------
const MENTION_PILL_STYLE: CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--ct3-mention-pill-gap, 6px)",
    margin: "0 var(--ct3-mention-pill-margin-x, 2px)",
    padding: "var(--ct3-mention-pill-padding, 3px 12px 3px 4px)",
    borderRadius: 90,
    backgroundColor: "rgba(95,243,184,0.10)",
    boxShadow: "inset 0px 1px 1px 0px #ffffff3b, 1px 2px 2px -1px #00000045",
    fontSize: "var(--ct3-mention-pill-font-size, 18px)",
    fontWeight: 500,
    lineHeight: 1,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
    color: MINT,
    WebkitTextFillColor: MINT,
};
const MENTION_PILL_ICON_STYLE: CSSProperties = {
    width: "var(--ct3-mention-pill-icon-size, 24px)",
    height: "var(--ct3-mention-pill-icon-size, 24px)",
    flexShrink: 0,
    padding: "var(--ct3-mention-pill-icon-padding, 2px)",
    boxSizing: "border-box",
    borderRadius: "50%",
    overflow: "hidden",
    backgroundColor: "rgba(0,0,0,0.38)",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
};
function MentionChip({ name }: {
    name: string;
}): ReactElement {
    const provider = MENTION_PROVIDER[name];
    return (
      <span style={MENTION_PILL_STYLE}>
        {provider ? (<span style={MENTION_PILL_ICON_STYLE}>
            <Cimage src={getProviderIconPath(provider)} alt={name} static animation="none" objectFit="contain" style={{ width: "74%", height: "74%" }}/>
          </span>) : null}
        {`@${name}`}
      </span>
    );
}
function formatBody(body: string): string {
    return body
        .replace(/(:)\s+(?=["“'A-Z])/g, "$1\n")
        .replace(/\s+(?=\d+\.\s)/g, "\n")
        .replace(/\s+(?=@[A-Za-z])/g, "\n")
        .replace(/(?<!\d)(?<!Prof)(?<!Dr)([.!?"”'])\s+(?=[A-Z@])/g, "$1\n")
        .replace(/\n{2,}/g, "\n")
        .trim();
}
function renderBody(body: string): ReactNode[] {
    return body.split(/(@[A-Za-z]+)/g).map((part, index) => {
        const name = part.startsWith("@") ? part.slice(1) : null;
        if (name)
            return <MentionChip key={index} name={name}/>;
        return part;
    });
}
const FILES_WRAP_STYLE: CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    marginTop: 6,
};
// ---------------------------------------------------------------------------
// Avatar + role tag + message
// ---------------------------------------------------------------------------
type MessageRectShapeLocal = ReturnType<typeof measureChatTeaser3Layout>["rects"][number];
interface AvatarCapsuleProps {
    comment: ScrollCommentData;
    layoutVersion?: MotionValue<number>;
    messageScroll?: MotionValue<number>;
    rect?: MessageRectShapeLocal;
}
function AvatarCapsuleIcon({ comment }: {
    comment: ScrollCommentData;
}): ReactNode {
    if (!comment.provider)
        return null;
    return (<Cimage src={getProviderIconPath(comment.provider)} alt={comment.name} static animation="none" objectFit="contain" style={{ width: "100%", height: "100%" }}/>);
}
function AvatarCapsuleStatic({ comment, }: {
    comment: ScrollCommentData;
}): ReactElement {
    return (
      <div style={AVATAR_SLOT_STYLE}>
        <div style={{
            ...AVATAR_CAPSULE_BASE_STYLE,
            height: "100%",
        }}>
          <div style={AVATAR_ICON_STYLE}>
            <AvatarCapsuleIcon comment={comment}/>
          </div>
          <span style={AVATAR_NAME_STYLE}>
            {comment.name}
          </span>
        </div>
      </div>
    );
}
function AvatarCapsuleMotion({ comment, layoutVersion, messageScroll, rect, }: {
    comment: ScrollCommentData;
    layoutVersion: MotionValue<number>;
    messageScroll: MotionValue<number>;
    rect: MessageRectShapeLocal;
}): ReactElement {
    const capsuleHeight = useTransform([messageScroll, layoutVersion], ([scrollTop]) => {
        const scrolledPast = (scrollTop as number) - rect.top;
        if (scrolledPast <= 0)
            return "100%";
        return Math.max(CIRCLE_SIZE, rect.height - scrolledPast);
    });
    const capsuleTranslateY = useTransform([messageScroll, layoutVersion], ([scrollTop]) => {
        const scrolledPast = (scrollTop as number) - rect.top;
        if (scrolledPast <= 0)
            return 0;
        const height = Math.max(CIRCLE_SIZE, rect.height - scrolledPast);
        return Math.min(scrolledPast, rect.height - height);
    });
    const nameOpacity = useTransform([messageScroll, layoutVersion], ([scrollTop]) => {
        const scrolledPast = (scrollTop as number) - rect.top;
        if (scrolledPast <= 0)
            return 1;
        const height = Math.max(CIRCLE_SIZE, rect.height - scrolledPast);
        const denominator = rect.height - CIRCLE_SIZE;
        return denominator > 0
            ? Math.max(0, Math.min(1, (height - CIRCLE_SIZE) / denominator))
            : 0;
    });
    return (
      <div style={AVATAR_SLOT_STYLE}>
        <motion.div style={{
            ...AVATAR_CAPSULE_BASE_STYLE,
            height: capsuleHeight,
            y: capsuleTranslateY,
        }}>
          <div style={AVATAR_ICON_STYLE}>
            <AvatarCapsuleIcon comment={comment}/>
          </div>
          <motion.span style={{
            ...AVATAR_NAME_STYLE,
            opacity: nameOpacity,
        }}>
            {comment.name}
          </motion.span>
        </motion.div>
      </div>
    );
}
function AvatarCapsule({ comment, layoutVersion, messageScroll, rect, }: AvatarCapsuleProps): ReactElement {
    if (messageScroll != null && layoutVersion != null && rect != null) {
        return (<AvatarCapsuleMotion comment={comment} layoutVersion={layoutVersion} messageScroll={messageScroll} rect={rect}/>);
    }
    return <AvatarCapsuleStatic comment={comment}/>;
}
function RoleTag({ role }: {
    role: string;
}): ReactElement {
    return (
      <div style={ROLE_TAG_WRAP_STYLE}>
        <div style={ROLE_TAG_BG_STYLE}/>
        <div style={ROLE_TAG_INNER_STYLE}>
          <div style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
        }}>
            <span style={{
            color: "rgba(255,255,255,0.3)",
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1,
            transform: "translateY(1px)",
        }}>
              ~
            </span>
            <span style={{
            color: MINT,
            fontSize: 22,
            fontWeight: 500,
            lineHeight: 1,
        }}>
              {role}
            </span>
          </div>
        </div>
      </div>
    );
}
interface ChatMessageProps {
    comment: ScrollCommentData;
    layoutVersion?: MotionValue<number>;
    messageScroll?: MotionValue<number>;
    rect?: MessageRectShapeLocal;
}
function isFakeCommentTextMessage(message: FakeCommentMessage): message is Extract<FakeCommentMessage, {
    kind: typeof FAKE_COMMENT_MESSAGE_KIND.Text;
}> {
    return message.kind === FAKE_COMMENT_MESSAGE_KIND.Text;
}
function getMessageFiles(message: FakeCommentMessage): readonly FakeCommentFileAttachment[] | undefined {
    if (isFakeCommentTextMessage(message)) {
        return message.files;
    }
    return [message];
}
export function ChatMessage({ comment, layoutVersion, messageScroll, rect, }: ChatMessageProps): ReactElement {
    const body = isFakeCommentTextMessage(comment.message)
        ? formatBody(comment.message.body)
        : "";
    const files = getMessageFiles(comment.message);
    return (
      <div data-el="Cchatteaser3/ChatMessage/Entry" style={MESSAGE_ROW_STYLE}>
        <AvatarCapsule comment={comment} layoutVersion={layoutVersion} messageScroll={messageScroll} rect={rect}/>
        <div style={MESSAGE_CONTENT_STYLE}>
          {comment.teamRole ? <RoleTag role={comment.teamRole}/> : null}
          <div style={MESSAGE_TEXT_WRAP_STYLE}>
            <p style={MESSAGE_TEXT_STYLE}>
              {renderBody(body)}
            </p>
          </div>
          {files && files.length > 0 ? (<div data-el="Cchatteaser3/ChatMessage/AttachmentList" style={FILES_WRAP_STYLE}>
              {files.map((file, index) => (<Chatteaser3FileChip key={`${file.fileName}-${index}`} file={file}/>))}
            </div>) : null}
        </div>
      </div>
    );
}
